import React, { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import AuthImage from "@/components/AuthImage";

const MIN_W = 420;
const MIN_H = 320;

/** Click-to-zoom viewer for help-center screenshots — also drag-resizable from the
 * bottom-right corner, and one-click "maximize" to fill the user's screen resolution. */
export default function ImageLightbox({ src, alt, open, onOpenChange }) {
  const [zoom, setZoom] = useState(1);
  const [size, setSize] = useState(() => ({
    width: Math.min(960, window.innerWidth * 0.9),
    height: Math.min(680, window.innerHeight * 0.85),
  }));
  const resizing = useRef(null);

  const onResizeMove = useCallback((e) => {
    if (!resizing.current) return;
    const { startX, startY, startW, startH } = resizing.current;
    const maxW = window.innerWidth * 0.98;
    const maxH = window.innerHeight * 0.95;
    setSize({
      width: Math.min(maxW, Math.max(MIN_W, startW + (e.clientX - startX))),
      height: Math.min(maxH, Math.max(MIN_H, startH + (e.clientY - startY))),
    });
  }, []);

  const onResizeEnd = useCallback(() => {
    resizing.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeEnd);
  }, [onResizeMove]);

  const onResizeStart = (e) => {
    e.preventDefault();
    resizing.current = { startX: e.clientX, startY: e.clientY, startW: size.width, startH: size.height };
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeEnd);
  };

  const maximize = () => setSize({ width: window.innerWidth * 0.97, height: window.innerHeight * 0.94 });

  return (
    <Dialog open={open} onOpenChange={(v) => { setZoom(1); onOpenChange(v); }}>
      <DialogContent
        style={{ width: size.width, height: size.height, maxWidth: "98vw", maxHeight: "95vh" }}
        className="p-4 gap-3 !flex !flex-col"
      >
        <div className="flex items-center justify-between pr-6 flex-shrink-0">
          <DialogTitle className="text-sm font-semibold text-slate-900 truncate">{alt}</DialogTitle>
          <DialogDescription className="sr-only">Aperçu zoomable de la capture d'écran {alt}</DialogDescription>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="lightbox-zoom-out"
              onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}
              className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
            >
              <ZoomOut size={15} />
            </button>
            <span className="text-xs text-slate-400 w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <button
              data-testid="lightbox-zoom-in"
              onClick={() => setZoom((z) => Math.min(3, +(z + 0.5).toFixed(1)))}
              className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
            >
              <ZoomIn size={15} />
            </button>
            <button
              data-testid="lightbox-maximize"
              title="Agrandir à la taille de l'écran"
              onClick={maximize}
              className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
            >
              <Maximize2 size={15} />
            </button>
          </div>
        </div>
        <div className="bg-slate-950 rounded-lg overflow-auto flex-1 flex items-center justify-center p-3">
          <AuthImage
            src={src}
            alt={alt}
            className="max-w-full transition-transform duration-150"
            style={{ transform: `scale(${zoom})`, transformOrigin: "center" }}
          />
        </div>
        <div
          data-testid="lightbox-resize-handle"
          onMouseDown={onResizeStart}
          title="Glisser pour redimensionner"
          className="absolute bottom-1.5 right-1.5 w-4 h-4 cursor-nwse-resize opacity-50 hover:opacity-90"
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path d="M15 15 L15 9 M15 15 L9 15 M15 11 L11 15 M15 7 L7 15" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </div>
      </DialogContent>
    </Dialog>
  );
}
